<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :disabled="disabled"
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    open-on-click
  >
    <template v-slot:activator="{ props }">
      <slot v-bind="{ props }" name="activator" />
    </template>

    <v-card rounded="lg" data-cy="dialog-createTranscript-modal">
      <v-card-title class="d-flex align-center justify-space-between"
                    data-cy="dialog-createTranscript-title">
        <span>{{ $t('routes.pod.menuSubtitles.generateSubtitles') }}</span>
        <v-btn variant="text" size="small" rounded="circle" icon
               @click="dialog = false" :aria-label="$t('common.close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-tabs v-model="activeTab" @update:model-value="handleTabChange">
        <v-tab key="generate" color="primary" rounded="0">{{ $t('common.generate') }}</v-tab>
        <v-tab key="create" color="primary" rounded="0" v-show="transcriptionContext === 'podView'">
          {{ $t('routes.pod.menuSubtitles.createBlank') }}
        </v-tab>
      </v-tabs>

      <v-card-text class="scrollable mt-4">
        <!-- Generate Tab Content -->
        <v-tabs-window v-model="activeTab">
          <v-tabs-window-item key="generate">
            <v-form ref="generateForm" @submit.prevent="stub()"
                    :disabled="noThingsSelected || !suitablePods || suitablePods.length === 0">
              <!-- Section 1: Service and Language Selection -->
              <v-card class="mb-2 pa-3" border tile elevation="0" rounded="0">
                <div data-cy="dialog-createTranscript-selectService">
                  <v-radio-group
                    v-model="selectedServiceId"
                    color="primary"
                    :label="$t('routes.pod.menuCreateTranscript.selectServiceToUse')"
                    class="mt-0 pt-0"
                    density="compact"
                    mandatory
                  >
                    <v-radio
                      v-for="option in textToSpeechServices"
                      :key="option.id"
                      :value="option.id"
                      :label="option.name"
                      data-cy="dialog-createTranscript-selectService-option"
                    />
                  </v-radio-group>
                </div>

                <div v-if="pods.length > 0">
                  <div>
                    <v-checkbox
                      v-model="autoDetect"
                      v-show="!autoDetectDisabled"
                      :disabled="autoDetectDisabled"
                      density="comfortable"
                      hide-details
                      :label="$t('routes.pod.menuCreateTranscript.autoLangIdent')"
                    />
                  </div>

                  <div v-show="selectedService && !autoDetect">
                    <v-autocomplete
                      v-model="languageCodes"
                      :items="languages"
                      item-title="name"
                      item-value="value"
                      :label="$t('routes.pod.menuCreateTranscript.language')"
                      :disabled="autoDetect"
                      variant="solo-filled"
                      density="comfortable"
                      :list-props="{
                        'tabindex': 0,
                      }"
                      flat
                      required
                      return-object
                    />
                  </div>
                </div>
              </v-card>

              <!-- Section 2: Output Options -->
              <v-card v-if="pods.length > 0" class="mb-2 pa-3" elevation="0" border tile
                      rounded="0">
                <div>
                  <v-label class="mb-1">
                    {{ $t('routes.pod.generateSubtitles.outputFileFormat') }}
                  </v-label>
                  <v-radio-group
                    v-model="selectedFileFormat"
                    class="mt-0 pt-0"
                    hide-details
                    mandatory
                    inline
                    data-cy="dialog-generateSubtitles-form-selectFileFormat"
                  >
                    <v-radio
                      v-for="option in subtitleFormats"
                      :key="option.id"
                      :value="option.id"
                      :label="option.label"
                      data-cy="dialog-generateSubtitles-form-selectFileFormat-option"
                    />
                  </v-radio-group>
                </div>

                <div v-if="this.pods.length === 1">
                  <v-text-field
                    v-model="fileName"
                    :placeholder="$t('routes.pod.menuSubtitles.filename')"
                    data-cy="dialog-generateSubtitles-form-fileName"
                    block variant="solo-filled"
                    density="comfortable"
                    flat
                    hide-details
                    class="mt-4"
                  />
                  <div class="font-italic">
                    <v-icon size="small">mdi-information-outline</v-icon>
                    {{ $t('routes.pod.generateSubtitles.autoAddExtension') }}
                  </div>
                </div>
              </v-card>

              <!-- Section 3: Cost Information -->
              <v-card v-if="pods.length > 0 && selectedService" class="mb-2 pa-3" elevation="0"
                      rounded="0" border tile>
                <div class="d-flex font-italic">
                  <div class="flex-grow-1">
                    {{ $t('routes.pod.menuCreateTranscript.mediaSelected') }}
                  </div>
                  <div>{{ videoDuration(summedDuration) }}</div>
                </div>

                <div class="d-flex font-italic">
                  <div class="flex-grow-1">{{ $t('routes.pod.serviceCost') }}</div>
                  <div>{{ summedCost }}</div>
                </div>

                <div class="d-flex font-italic">
                  <div class="flex-grow-1">{{ $t('routes.pod.yourAvailableCredits') }}</div>
                  <div>{{ creditsLeft }}</div>
                </div>

                <div class="d-flex font-italic">
                  <div class="flex-grow-1">{{ $t('routes.pod.estimatedRemainingCredits') }}</div>
                  <div>{{ creditsLeftAfterJob }}</div>
                </div>

                <v-divider class="my-2" aria-hidden="true" />

                <div v-if="selectedService && selectedService.alias === 'aws-transcribe'"
                     class="d-flex mt-2">
                  <v-icon size="small" class="mr-1">mdi-information-outline</v-icon>
                  <p class="font-italic mt-0 mb-1">
                    {{ $t('routes.pod.menuCreateTranscript.minimumCharge') }}
                  </p>
                </div>

                <div v-if="selectedService && selectedService.alias === 'google-transcribe'"
                     class="d-flex mt-2">
                  <v-icon size="small" class="mr-1">mdi-information-outline</v-icon>
                  <p class="font-italic mt-0 mb-1">
                    {{ $t('routes.pod.menuCreateTranscript.googleMinimumCharge') }}
                  </p>
                </div>
              </v-card>

              <!-- Additional Content -->
              <div v-if="creditsLeftAfterJob < 0" class="my-2">
                <template v-if="$canPurchaseResources()">
                  <div>{{ $t('routes.pod.menuCreateTranscript.wantToBuyBundle') }}</div>
                  <div class="d-flex justify-space-around">
                    <v-btn v-on:click="dialog = false" elevation=0 color="primary">
                      {{ $t('common.no') }}
                    </v-btn>
                    <v-btn @click="navigateToUsage" elevation=0 color="primary">
                      {{ $t('common.yes') }}
                    </v-btn>
                  </div>
                </template>
                <template v-else>
                  <div class="bg-warning pa-2">{{ $t('common.contactTenantAdminForCredits') }}</div>
                </template>
              </div>

              <!-- Selected Pods List -->
              <v-card class="mb-2" elevation="0" rounded="0" border tile>
                <v-list density="compact" class="bg-list">
                  <v-list-item v-for="item in pods" :key="item.id" class="bg-list">
                    <template v-slot:prepend>
                      <v-icon v-if="isPodRunnable(item)" color="success" style="align-self: center">
                        mdi-check-bold
                      </v-icon>
                      <v-icon v-else color="error" style="align-self: center">mdi-close-thick
                      </v-icon>
                    </template>

                    <v-list-item-title class="d-flex justify-space-between" data-cy="dialog-createTranscript-runnablePodName">
                      <span>{{ item.name }}</span>
                      <span
                        v-if="isPodRunnable(item) && getUtilityPod(item).metadata && typeof getUtilityPod(item).metadata.duration == 'number'"
                        class="mr-4"
                      >
                        {{ videoDuration(getUtilityPod(item).metadata.duration) }}
                      </span>
                    </v-list-item-title>
                    <v-list-item-subtitle
                      v-if="getUtilityPod(item) && getUtilityPod(item).error"
                      class="text-error"
                    >
                      {{ $replaceT(getUtilityPod(item).error.code, getUtilityPod(item).error.params, getUtilityPod(item).error.content) }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-if="!isSuitableItem(item)" class="text-error">
                      {{ $t('routes.pod.menuCreateTranscript.notSuitableFormat') }}
                    </v-list-item-subtitle>

                    <template  v-slot:append>
                      <v-btn
                        v-if="pods.length > 1"
                        icon="mdi-close"
                        size="xs-small"
                        variant="text"
                        @click="toggleSelection(item)"
                      />
                    </template>
                  </v-list-item>
                </v-list>
              </v-card>

              <!-- Status Messages -->
              <div v-show="successMessage" class="bg-success my-2"
                   data-cy="dialog-createTranscript-successMessage">{{ successMessage }}
              </div>
              <div v-show="errorMessage" class="bg-error my-2"
                   data-cy="dialog-createTranscript-errorMessage">{{ errorMessage }}
              </div>
              <div v-if="errorTranslatable" class="bg-error my-2">
                {{ $replaceT(errorTranslatable.code, errorTranslatable.params, errorTranslatable.content) }}
              </div>
            </v-form>
          </v-tabs-window-item>

          <!-- Create Tab Content -->
          <v-tabs-window-item key="create">
            <v-card class="mb-2 pa-3" border tile elevation="0">
              <v-form ref="createForm" v-on:submit.prevent="stub()">
                <v-label class="text-subtitle-1 mb-1">
                  {{ $t('routes.pod.generateSubtitles.outputFileFormat') }}
                </v-label>
                <v-radio-group
                  v-model="createFiletype"
                  mandatory
                  inline
                  class="mt-0 pt-0"
                >
                  <v-radio label="SRT" value="srt"
                           data-cy="dialog-createSubtitle-srtRadio"></v-radio>
                  <v-radio label="VTT" value="vtt"
                           data-cy="dialog-createSubtitle-vttRadio"></v-radio>
                </v-radio-group>

                <v-text-field
                  type="text"
                  v-model="createFilename"
                  :rules="filenameRules"
                  :placeholder="$t('routes.pod.menuTranscript.exportDialogFilename')"
                  required
                  variant="solo-filled"
                  block
                  flat
                  data-cy="dialog-exportSubtitle-fileName"
                />

                <div v-show="createErrorMessage" class="bg-error"
                     data-cy="dialog-exportSubtitle-fileTypeError">
                  {{ createErrorMessage }}
                </div>

                <div class="bg-error" v-if="createErrorTranslatable">
                  {{ $replaceT(createErrorTranslatable.code, createErrorTranslatable.params, createErrorTranslatable.content) }}
                </div>
              </v-form>
            </v-card>
          </v-tabs-window-item>
        </v-tabs-window>
      </v-card-text>

      <v-card-actions>
        <v-btn
          v-if="showingGenerateAudioBtn"
          color="primary"
          variant="flat"
          data-cy="pod-file-list-generateAudio"
          @click="showGenerateAudioDialog"
        >
          {{ $t('routes.pod.menuGenerateAudio.generateAudio') }}
        </v-btn>

        <v-spacer/>

        <div
          v-show="actionType === 'generate' && creditsLeftAfterJob >= 0 && selectedServiceId !== -1"
          class="mx-2 text-center"
        >
          <v-btn
            :disabled="isRunJobsDisabled"
            v-on:click.prevent="generateSubtitles()"
            color="primary"
            variant="flat"
            data-cy="dialog-createTranscript-runJobs-btn"
          >
            {{ preventDoubleclick ? `${$t('common.generate')} (${countdownSeconds})` : $t('common.generate') }}
          </v-btn>
        </div>
        <div v-show="actionType === 'create'" class="mx-2 text-center">
          <v-btn
            color="primary"
            variant="flat"
            @click="createSubtitleFile()"
            data-cy="dialog-exportSubtitle-ok"
          >
            {{ $t('common.create') }}
          </v-btn>
        </div>

        <v-btn @click="dialog = false" data-cy="dialog-createTranscript-close-btn">
          {{ $t('common.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <GenerateAudioDialog
    v-if="showingGenerateAudioBtn"
    v-model="showingGenerateAudioDialog"
    :pod="pods[0]">
  </GenerateAudioDialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useServiceStore } from '@/stores/ServiceStore.js'
import GenerateAudioDialog from "../../../pods2/components/dialogs/GenerateAudioDialog.vue";
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import { videoDuration } from '@/components/formatters.js';
import DialogModel from '@/components/mixin/DialogModel.js';

export default {
  mixins: [DialogActionKeys, DialogModel],
  name: "TranscribeSelectedDialog",
  components: {
    GenerateAudioDialog,
  },
  props: {
    pods: {
      type: Array,
      required: true
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    transcriptionContext: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      activeTab: 0,
      actionType: 'generate',

      // Generate tab data
      errorMessage: '',
      errorTranslatable: null,
      successMessage: '',
      preventDoubleClick: false,

      languageCodes: [],
      autoDetect: true,
      autoDetectDisabled: true,

      serviceOptions: null,
      selectedServiceId: null,
      selectedServiceRules: [
        (v) => !!v || this.$t('routes.pod.menuCreateTranscript.serviceProviderRequired'),
      ],

      transcriptname: "",
      transcriptnameRules: [
        (v) => !!v || this.$t('routes.pod.menuTranscript.invalidTranscriptName')
      ],
      subtitleFormats: [
        { id: 'srt', label: 'SRT' },
        { id: 'vtt', label: 'VTT' },
      ],
      fileName: '',
      selectedFileFormat: 'srt',

      // Create tab data
      createFilename: "",
      filenameRules: [
        (v) => !!v || this.$t('routes.pod.menuTranscript.exportDialogInvalidName')
      ],
      createFiletype: "srt",
      createErrorMessage: "",
      createErrorTranslatable: null,
      countdownSeconds: 10,
      countdownInterval: null,
      showingGenerateAudioDialog: false,
    };
  },
  beforeUnmount() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  },
  mounted() {
    const serviceStore = useServiceStore();
    serviceStore.resetUtilityPods();

    const podStore = usePodStore();
    podStore.selectedPodIds = this.pods.map((pod) => pod.id);
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.resetForm();
        this.updatePodsUtility();
      }
    },
    selectedService(newServices) {
      if (!this.dialog) {
        return false;
      }

      this.updatePodsUtility();

      if (newServices && newServices.alias) {
        this.refreshServiceOptions(newServices.alias);
      }
    },
    showingGenerateAudioBtn() {
      if (this.dialog) {
        this.updatePodsUtility();
      }
    },
    actionType(newValue) {
      if (newValue === 'create') {
        if (this.$refs?.createForm) {
          this.$nextTick(() => {
            this.$refs.createForm.resetValidation();
          });
        }
      }
    },
  },
  methods: {
    videoDuration,
    stub() {
      console.log('Form submitted prevent.');
    },
    handleTabChange(tab) {
      this.actionType = tab === 0 ? 'generate' : 'create';
    },
    resetForm() {
      if (this.$refs.generateForm) {
        this.$refs.generateForm.reset();
      }

      if (this.$refs.createForm) {
        this.$refs.createForm.reset();
      }

      this.$nextTick(() => {
        // Reset generate tab
        this.errorMessage = '';
        this.errorTranslatable = null;
        this.successMessage = '';
        this.preventDoubleclick = false;

        this.languageCodes = [];
        this.autoDetect = true;
        this.autoDetectDisabled = true;

        this.serviceOptions = null;
        this.selectedServiceId = this.textToSpeechServices[0].id;
        this.transcriptname = '';

        // Reset create tab
        this.createFilename = "";
        this.createFiletype = "srt";
        this.createErrorMessage = "";
        this.createErrorTranslatable = null;

        const serviceStore = useServiceStore();
        serviceStore.resetUtilityPods();
      });
    },
    toggleSelection(pod) {
      const podStore = usePodStore();
      podStore.toggleSelectedPod(pod);

      this.updatePodsUtility();
    },
    updatePodsUtility() {
      this.errorMessage = '';
      this.errorTranslatable = '';
      if (this.suitablePods.length === 0) {
        const serviceStore = useServiceStore();
        serviceStore.resetUtilityPods();
        return;
      }

      if (!this.selectedService || !this.selectedService.alias) {
        return;
      }

      const payload = {
        serviceAlias: this.selectedService.alias,
        pods: this.suitablePods,
      };

      const serviceStore = useServiceStore();
      serviceStore.updatePodsUtility(payload);
    },
    generateSubtitles() {
      this.errorMessage = "";
      this.errorTranslatable = "";
      this.successMessage = "";

      if (!this.selectedService.alias) {
        return;
      }

      let languages = [];
      if (!this.autoDetect) {
        if (this.languageCodes && this.languageCodes.value) {
          languages.push(this.languageCodes.value);
        }

        if (languages.length === 0) {
          this.errorMessage = this.$t("routes.pod.menuCreateTranscript.languageRequired");
          return;
        }
      }

      const payload = {
        serviceAlias: this.selectedService.alias,
        utilityPods: this.runnablePods,
        params: {
          auto: this.autoDetect,
          languages,
          generateSubtitles: {
            format: this.selectedFileFormat,
            filename: this.fileName || '',
          },
        },
      };

      let self = this;
      this.preventDoubleclick = true;

      // Set up countdown
      this.countdownSeconds = 10;

      // Clear any existing interval
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }

      // Create countdown interval
      this.countdownInterval = setInterval(() => {
        this.countdownSeconds--;

        if (this.countdownSeconds <= 0) {
          clearInterval(this.countdownInterval);
          this.preventDoubleclick = false;
        }
      }, 1000);

      // Replace your existing timeout with this:
      setTimeout(() => {
        this.preventDoubleclick = false;
        clearInterval(this.countdownInterval);
      }, 10000);

      const serviceStore = useServiceStore();
      serviceStore.runService(payload)
          .then(() => {
            if (self.utilityPodsErrors.length === 0) {
              self.successMessage = self.$t("routes.pod.menuCreateTranscript.operationQueued");
            }
          })
          .catch((error) => {
            if (error && error.response && error.response.data && error.response.data.error && error.response.data.error.message) {
              const apiError = error.response.data.error.message;
              if (typeof apiError === 'object') {
                self.errorTranslatable = apiError;
              } else {
                self.errorMessage = apiError;
              }
            } else {
              self.errorMessage = self.$t("backend.system-error");
              console.log(error);   // unhandled error
            }

            self.preventDoubleClick = false;
            clearInterval(self.countdownInterval);
          });
    },
    refreshServiceOptions(serviceAlias) {
      let self = this;

      const serviceStore = useServiceStore();
      serviceStore.refreshServiceOptions(serviceAlias)
          .then((options) => {
            self.serviceOptions = options;
          })
          .then(() => {
            if (serviceAlias === 'aws-transcribe') {
              self.autoDetect = true;
              self.autoDetectDisabled = false;
            } else {
              self.autoDetect = false;
              self.autoDetectDisabled = true;
            }
          });
    },
    createSubtitleFile() {
      this.createErrorMessage = "";
      this.createErrorTranslatable = null;

      const validForm = this.$refs.createForm.validate();
      if (!validForm) {
        return;
      }

      let regexp = new RegExp("\\." + this.createFiletype + "$", "gi");
      if (!regexp.test(this.createFilename)) {
        this.createFilename += "." + this.createFiletype;
      }

      const thePod = this.pods && this.pods[0] ? this.pods[0] : null;

      let self = this;
      const data = {
        podID: thePod.id,
        payload: {
          filename: this.createFilename,
          filetype: this.createFiletype,
        }
      };

      const podStore = usePodStore();
      podStore.createSubtitleAsset(data)
          .then((createdAsset) => {
            createdAsset;
            self.dialog = false;
          })
          .catch((error) => {
            if (error && error.response && error.response.data && error.response.data.error) {
              const apiError = error.response.data.error.message;
              if (typeof apiError === 'object') {
                self.createErrorTranslatable = apiError;
              } else {
                self.createErrorMessage = apiError;
              }
            } else {
              self.createErrorMessage = "Error!";
              console.log(error);   // unhandled error
            }
          })
          .finally(() => {
            self.$emit('subtitleCreated');
          });
    },
    showGenerateAudioDialog: function() {
      this.showingGenerateAudioDialog = true;
      this.dialog = false;
    },
    navigateToUsage() {
      this.dialog = false;
      this.$router.push('/console/usage');
    },
  },
  computed: {
    // Generate tab computed
    noThingsSelected() {
      return this.suitablePods.length === 0;
    },
    selectedService() {
      return this.textToSpeechServices.find((x) => x.id === this.selectedServiceId);
    },
    creditsLeft() {
      if (this.getUtilityPods && this.getUtilityPods[0] && this.getUtilityPods[0].metadata) {
        return this.getUtilityPods[0].metadata.available;
      }

      return 0;
    },
    creditsLeftAfterJob() {
      return this.creditsLeft - this.summedCost;
    },
    textToSpeechServices() {
      const serviceStore = useServiceStore();
      return serviceStore.getTextToSpeech;
    },
    suitablePods() {
      const podStore = usePodStore();
      let videoPods = podStore.getSelectedVideoPods;
      let audioPods = podStore.getSelectedAudioPods;

      return videoPods.concat(audioPods);
    },
    isSuitableItem() {
      return (item) => {
        return Boolean(this.suitablePods.find((x) => x.id === item.id));
      };
    },
    getUtilityPods() {
      const serviceStore = useServiceStore();
      return serviceStore.getUtilityPods;
    },
    runnablePods() {
      const serviceStore = useServiceStore();
      return serviceStore.getRunnablePods;
    },
    utilityPodsErrors() {
      const serviceStore = useServiceStore();
      return serviceStore.getUtilityPodsErrors;
    },
    isPodRunnable() {
      return (pod) => {
        const serviceStore = useServiceStore();
        return serviceStore.isPodRunnable(pod);
      };
    },
    getUtilityPod() {
      return (pod) => {
        const serviceStore = useServiceStore();
        return serviceStore.getUtilityPod(pod);
      };
    },
    summedDuration() {
      let sum = 0;
      for (let i = 0; i < this.getUtilityPods.length; i++) {
        if (this.getUtilityPods[i].metadata &&
            this.getUtilityPods[i].metadata.duration !== null &&
            this.getUtilityPods[i].metadata.duration !== undefined) {
          sum += this.getUtilityPods[i].metadata.duration;
        }
      }
      return sum;
    },
    summedCost() {
      let sum = 0;
      for (let i = 0; i < this.getUtilityPods.length; i++) {
        if (this.getUtilityPods[i].metadata &&
            this.getUtilityPods[i].metadata.cost !== null &&
            this.getUtilityPods[i].metadata.cost !== undefined) {
          sum += this.getUtilityPods[i].metadata.cost;
        }
      }
      return sum;
    },
    isRunJobsDisabled() {
      if (!this.selectedService ||
          this.runnablePods.length === 0 ||
          this.runnablePods.length !== this.pods.length ||
          this.preventDoubleClick === true
      ) {
        return true;
      }

      return false;
    },
    languages() {
      if (this.selectedService && this.serviceOptions && this.serviceOptions.languages) {
        return this.serviceOptions.languages;
      }
      return [];
    },
    showingGenerateAudioBtn() {
      // there should be only 1 pod selected
      if (!Array.isArray(this.pods) || this.pods.length !== 1) {
        return false;
      }

      // it should be suitable pod
      if (this.suitablePods.length !== 1) {
        return false;
      }

      const pod = this.pods && this.pods[0] ? this.pods[0] : null;

      const noAudioErrors = ['ERR40', 'ERR12'];
      const utilityPod = this.getUtilityPod(pod);

      if (utilityPod &&
          utilityPod.error &&
          noAudioErrors.indexOf(utilityPod.error.code) >= 0) {
        return true;
      }
      return false;
    },
  }
};
</script>

<style>
.v-theme--dark.v-tabs-items {
  background-color: inherit;
}
</style>
